import { useEffect, useState, useCallback } from 'react';
import {
  decodeNsec, pubkeyFromSecret, storeNsec, loadStoredNsec,
  clearStoredNsec, installNsecSigner, releaseAppSigner,
  connectBunker, loadBunkerConfig, clearBunkerConfig, installBunkerSigner,
  isNsecEncrypted, isNsecUnlocked, unlockNsec,
  type BunkerConfig,
} from '../lib/nsecSigner';
import { loadBusinessSecret, getBusinessIdentity } from '../lib/identities';
import { fetchProfile } from './useNostrQuery';

// When the user switches INTO a business Page, we stash the personal login
// here so "switch back" can restore it without re-authenticating.
const OWNER_STASH_KEY = 'bchnostr:owner-session';

interface OwnerStash {
  pubkey: string;
  method: 'extension' | 'nsec' | 'bunker';
}

export interface NostrUser {
  pubkey: string;
  name?: string;
  picture?: string;
  about?: string;
  nip05?: string;
  banner?: string;
  lud16?: string;
  bchAddress?: string;
  loginMethod?: 'extension' | 'nsec' | 'bunker' | 'business';
  /** True when the active identity is a business Page (app-held key). */
  isBusiness?: boolean;
  /** For a business Page, the personal pubkey that operates it. */
  ownerPubkey?: string;
}

export function useCurrentUser() {
  const [user, setUser] = useState<NostrUser | null>(null);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  // True when an encrypted nsec is stored but hasn't been unlocked this
  // session — the app should prompt for the PIN rather than treat the user
  // as logged out.
  const [nsecLocked, setNsecLocked] = useState(false);
  const [lockedPubkey, setLockedPubkey] = useState<string | null>(null);

  // ── Hydrate user from relay profile (kind 0) ─────────────────
  // Fetches the profile from Nostr relays and merges it into the
  // user state + localStorage cache. This ensures nsec users (and
  // first-time extension users) see their name/picture/bio.
  const hydrateFromRelay = useCallback((pubkey: string, loginMethod: 'extension' | 'nsec') => {
    fetchProfile(pubkey).then((relayProfile) => {
      if (!relayProfile) return;
      const merged: Partial<NostrUser> = {};
      if (relayProfile.name || relayProfile.display_name)
        merged.name = relayProfile.display_name || relayProfile.name;
      if (relayProfile.picture) merged.picture = relayProfile.picture;
      if (relayProfile.about) merged.about = relayProfile.about;
      if (relayProfile.banner) merged.banner = relayProfile.banner;
      if (relayProfile.nip05) merged.nip05 = relayProfile.nip05;
      if (relayProfile.lud16) merged.lud16 = relayProfile.lud16;
      // Also read bch_address from kind 0 profile if present
      if (relayProfile.bch_address) merged.bchAddress = relayProfile.bch_address;

      if (Object.keys(merged).length === 0) return;

      // Merge into local cache — relay data fills blanks, local data wins for non-empty fields
      const existing = (() => {
        try { return JSON.parse(localStorage.getItem('bchnostr:profile:' + pubkey) || '{}'); } catch { return {}; }
      })();
      const updated = { ...merged, ...existing };
      localStorage.setItem('bchnostr:profile:' + pubkey, JSON.stringify(updated));

      // Update React state — only fill in fields not already set
      setUser(prev => {
        if (!prev || prev.pubkey !== pubkey) return prev;
        return {
          ...prev,
          name: prev.name || merged.name,
          picture: prev.picture || merged.picture,
          about: prev.about || merged.about,
          banner: prev.banner || merged.banner,
          nip05: prev.nip05 || merged.nip05,
          lud16: prev.lud16 || merged.lud16,
          bchAddress: prev.bchAddress || merged.bchAddress,
        };
      });
    }).catch(() => {});
  }, []);

  // ── Post-login reload helper ─────────────────────────────────
  // After persisting login to localStorage, reload so every
  // component and Nostr subscription starts fresh with the user.
  const finishLogin = useCallback(() => {
    window.location.href = '/';
  }, []);

  // ── Extension login (nos2x, Alby, etc.) ──────────────────────
  const loginWithExtension = useCallback(async () => {
    try {
      // If a previous session locked an app-held signer, release it so the
      // real extension takes over window.nostr.
      releaseAppSigner();
      if (!(window as any).nostr) {
        throw new Error('No Nostr extension found');
      }
      const pubkey = await (window as any).nostr.getPublicKey();
      localStorage.setItem('bchnostr:current-user', pubkey);
      localStorage.setItem('bchnostr:login-method', 'extension');
      finishLogin();
    } catch (e) {
      console.error('Extension login failed:', e);
      throw e;
    }
  }, [finishLogin]);

  // ── Bunker / NIP-46 login ─────────────────────────────────────
  const loginWithBunker = useCallback(async (bunkerUrl: string, onProgress?: (msg: string) => void) => {
    try {
      const config = await connectBunker(bunkerUrl, onProgress);
      localStorage.setItem('bchnostr:current-user', config.userPubkey);
      localStorage.setItem('bchnostr:login-method', 'bunker');
      finishLogin();
    } catch (e) {
      console.error('Bunker login failed:', e);
      throw e;
    }
  }, [finishLogin]);

  // ── nsec login ───────────────────────────────────────────────
  const loginWithNsec = useCallback(async (nsec: string) => {
    try {
      const secretKey = decodeNsec(nsec);
      const pubkey = installNsecSigner(secretKey);
      storeNsec(nsec);
      localStorage.setItem('bchnostr:current-user', pubkey);
      localStorage.setItem('bchnostr:login-method', 'nsec');
      finishLogin();
    } catch (e) {
      console.error('nsec login failed:', e);
      throw e;
    }
  }, [finishLogin]);

  // ── Switch INTO a business Page ──────────────────────────────
  // Installs the business's app-held signer, stashes the current personal
  // session so we can return to it, points bchnostr:current-user at the
  // business, and reloads. Because every hook keys off current-user, the
  // whole app re-bootstraps as the business (including its own per-pubkey
  // tip wallet).
  const switchToBusiness = useCallback((businessPubkey: string) => {
    const secret = loadBusinessSecret(businessPubkey);
    const identity = getBusinessIdentity(businessPubkey);
    if (!secret || !identity) {
      throw new Error('This business Page is not available on this device.');
    }

    // Stash the personal session ONLY if we're currently on it (never
    // overwrite the stash when hopping business → business).
    const curMethod = localStorage.getItem('bchnostr:login-method');
    if (curMethod && curMethod !== 'business') {
      const curPubkey = localStorage.getItem('bchnostr:current-user') || '';
      const stash: OwnerStash = {
        pubkey: curPubkey,
        method: curMethod === 'nsec' ? 'nsec' : curMethod === 'bunker' ? 'bunker' : 'extension',
      };
      localStorage.setItem(OWNER_STASH_KEY, JSON.stringify(stash));
    }

    installNsecSigner(secret);
    localStorage.setItem('bchnostr:current-user', businessPubkey);
    localStorage.setItem('bchnostr:login-method', 'business');
    finishLogin();
  }, [finishLogin]);

  // ── Switch BACK to the operating personal account ────────────
  const switchToPersonal = useCallback(() => {
    let stash: OwnerStash | null = null;
    try {
      const raw = localStorage.getItem(OWNER_STASH_KEY);
      stash = raw ? JSON.parse(raw) as OwnerStash : null;
    } catch { stash = null; }

    if (!stash?.pubkey) {
      // No stash (e.g. hard reload while on a business) — fall back to a
      // clean re-login rather than guessing.
      setShowLoginDialog(true);
      return;
    }

    localStorage.removeItem(OWNER_STASH_KEY);
    localStorage.setItem('bchnostr:current-user', stash.pubkey);
    localStorage.setItem('bchnostr:login-method', stash.method);
    finishLogin();
  }, [finishLogin]);

  // ── Show login dialog (lets user choose method) ──────────────
  const login = useCallback(async () => {
    // Always show the dialog so users can choose their preferred method
    setShowLoginDialog(true);
  }, []);

  // ── Logout ───────────────────────────────────────────────────
  const logout = useCallback(() => {
    const method = localStorage.getItem('bchnostr:login-method');
    if (method === 'nsec') {
      clearStoredNsec();
      releaseAppSigner();
      delete (window as any).nostr;
    }
    if (method === 'bunker') {
      clearBunkerConfig();
      sessionStorage.removeItem('bchnostr:bunker-session-key');
      releaseAppSigner();
      delete (window as any).nostr;
    }
    if (method === 'business') {
      // The business signer is app-held; release the lock + drop it from
      // window, but keep the stored key so the owner can act as the Page
      // again after re-login.
      releaseAppSigner();
      delete (window as any).nostr;
    }
    setUser(null);
    localStorage.removeItem('bchnostr:current-user');
    localStorage.removeItem('bchnostr:login-method');
    localStorage.removeItem(OWNER_STASH_KEY);
  }, []);

  // ── Handle Amber NIP-55 callback ─────────────────────────────
  // Amber redirects back with ?amber_pubkey=<hex> after signing
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const amberPubkey = params.get('amber_pubkey');
    if (!amberPubkey || amberPubkey.length !== 64) return;

    // Clean the URL
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);

    // Install a lightweight Amber signer — opens Amber for each signature
    const makeAmberSigner = (pubkey: string) => ({
      getPublicKey: async () => pubkey,
      signEvent: async (event: any) => {
        // NIP-55: open Amber to sign, copy result
        const eventJson = JSON.stringify(event);
        const callbackUrl = `${window.location.origin}/?amber_signed=`;
        window.location.href = `nostrsigner:${encodeURIComponent(eventJson)}?compressionType=none&returnType=event&type=sign_event&callbackUrl=${encodeURIComponent(callbackUrl)}`;
        // Return the event as-is for now; Amber will redirect with signed event
        return event;
      },
      nip04: {
        encrypt: async (recipientPubkey: string, plaintext: string) => {
          const callbackUrl = `${window.location.origin}/?amber_result=`;
          window.location.href = `nostrsigner:${encodeURIComponent(plaintext)}?pubkey=${recipientPubkey}&compressionType=none&returnType=signature&type=nip04_encrypt&callbackUrl=${encodeURIComponent(callbackUrl)}`;
          return '';
        },
        decrypt: async (senderPubkey: string, ciphertext: string) => {
          const callbackUrl = `${window.location.origin}/?amber_result=`;
          window.location.href = `nostrsigner:${encodeURIComponent(ciphertext)}?pubkey=${senderPubkey}&compressionType=none&returnType=signature&type=nip04_decrypt&callbackUrl=${encodeURIComponent(callbackUrl)}`;
          return '';
        },
      },
    });

    // Amber is a redirect-based signer (not app-held); ensure any prior
    // app-signer lock is released before we install it.
    releaseAppSigner();
    (window as any).nostr = makeAmberSigner(amberPubkey);
    let profile: any = {};
    try { const stored = localStorage.getItem('bchnostr:profile:' + amberPubkey); profile = stored ? JSON.parse(stored) : {}; } catch { profile = {}; }
    setUser({ pubkey: amberPubkey, ...profile, loginMethod: 'extension' });
    localStorage.setItem('bchnostr:current-user', amberPubkey);
    localStorage.setItem('bchnostr:login-method', 'extension');
    setShowLoginDialog(false);
    hydrateFromRelay(amberPubkey, 'extension');
  }, [hydrateFromRelay]);

  // ── Restore session on mount ─────────────────────────────────
  useEffect(() => {
    const savedPubkey = localStorage.getItem('bchnostr:current-user');
    const savedMethod = localStorage.getItem('bchnostr:login-method');

    if (!savedPubkey) return;

    let profile: any = {};
    try { const stored = localStorage.getItem('bchnostr:profile:' + savedPubkey); profile = stored ? JSON.parse(stored) : {}; } catch { profile = {}; }

    if (savedMethod === 'business') {
      // Active identity is an app-held business Page. Install its signer
      // from the identities store; if the key is gone (e.g. storage
      // cleared), fall back to the operating personal account.
      const secretKey = loadBusinessSecret(savedPubkey);
      const identity = getBusinessIdentity(savedPubkey);
      if (secretKey && identity) {
        installNsecSigner(secretKey);
        setUser({
          pubkey: savedPubkey,
          ...profile,
          name: profile.name || identity.name,
          picture: profile.picture || identity.picture,
          loginMethod: 'business',
          isBusiness: true,
          ownerPubkey: identity.ownerPubkey,
        });
        if (!profile.name && !profile.picture) hydrateFromRelay(savedPubkey, 'nsec');
      } else {
        // Lost the key — return to the stashed personal session if we can.
        let stash: OwnerStash | null = null;
        try {
          const raw = localStorage.getItem(OWNER_STASH_KEY);
          stash = raw ? JSON.parse(raw) as OwnerStash : null;
        } catch { stash = null; }
        if (stash?.pubkey) {
          localStorage.setItem('bchnostr:current-user', stash.pubkey);
          localStorage.setItem('bchnostr:login-method', stash.method);
        } else {
          localStorage.removeItem('bchnostr:current-user');
          localStorage.removeItem('bchnostr:login-method');
        }
        localStorage.removeItem(OWNER_STASH_KEY);
      }
    } else if (savedMethod === 'nsec') {
      // Encrypted + not yet unlocked this session → surface the lock so the
      // UI can prompt for the PIN. Do NOT clear the login (the key is still
      // there, just sealed).
      if (isNsecEncrypted() && !isNsecUnlocked()) {
        setNsecLocked(true);
        setLockedPubkey(savedPubkey);
        return;
      }
      const nsec = loadStoredNsec();
      if (nsec) {
        try {
          const secretKey = decodeNsec(nsec);
          installNsecSigner(secretKey);
          setUser({ pubkey: savedPubkey, ...profile, loginMethod: 'nsec' });
          if (!profile.name && !profile.picture) hydrateFromRelay(savedPubkey, 'nsec');
        } catch {
          clearStoredNsec();
          localStorage.removeItem('bchnostr:current-user');
        }
      }
    } else if (savedMethod === 'bunker') {
      // Restore bunker signer using stored config + session key
      const config = loadBunkerConfig();
      if (config) {
        installBunkerSigner(config);
        setUser({ pubkey: savedPubkey, ...profile, loginMethod: 'bunker' });
        if (!profile.name && !profile.picture) hydrateFromRelay(savedPubkey, 'extension');
      } else {
        localStorage.removeItem('bchnostr:current-user');
        localStorage.removeItem('bchnostr:login-method');
      }
    } else {
      setUser({ pubkey: savedPubkey, ...profile, loginMethod: 'extension' });
      if (!profile.name && !profile.picture) hydrateFromRelay(savedPubkey, 'extension');
    }
  }, []);

  // ── Unlock an encrypted nsec with the PIN, then finish restoring ──
  const unlockWithPin = useCallback(async (pin: string) => {
    const nsec = await unlockNsec(pin); // throws on wrong PIN
    const secretKey = decodeNsec(nsec);
    const pubkey = installNsecSigner(secretKey);
    let profile: any = {};
    try {
      const stored = localStorage.getItem('bchnostr:profile:' + pubkey);
      profile = stored ? JSON.parse(stored) : {};
    } catch { profile = {}; }
    setUser({ pubkey, ...profile, loginMethod: 'nsec' });
    setNsecLocked(false);
    setLockedPubkey(null);
    if (!profile.name && !profile.picture) hydrateFromRelay(pubkey, 'nsec');
  }, [hydrateFromRelay]);

  const updateProfile = useCallback((profile: Partial<NostrUser>) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...profile };
      // Save the FULL merged profile to localStorage, not just the partial —
      // otherwise fields not in the partial get wiped on the next save.
      const { pubkey, loginMethod, ...storable } = updated;
      localStorage.setItem('bchnostr:profile:' + prev.pubkey, JSON.stringify(storable));
      return updated;
    });
  }, []);

  return {
    user,
    login,
    loginWithExtension,
    loginWithNsec,
    loginWithBunker,
    logout,
    updateProfile,
    switchToBusiness,
    switchToPersonal,
    showLoginDialog,
    setShowLoginDialog,
    nsecLocked,
    lockedPubkey,
    unlockWithPin,
  };
}

